import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Button,
  Stack,
  VStack,
  Text,
  Heading,
  Spinner,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  useBreakpointValue,
} from '@chakra-ui/react';
import DatePicker from 'react-datepicker';
import Footer from '../../components/Footer';
import ModalSpinner from '../../components/ModalSpinner';
import CustomDatePicker from '../../components/CustomDatePicker';
import { db, getMenu, auth } from '../../api';
import ProductStatus from './ProductStatus';
import TurnSelector from './TurnSelector';
import { Navigate, Link } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import useProductQuantitiesChanges from './useProductQuantitiesChanges';
import menuUtils from '../../utils/menu-utils';

const ORDER = 'order';

const calculateMinDelivered = (order, menuId) => {
  const menuIdItems = order.filter((item) => item.menuId === menuId);
  let minDelivered = menuIdItems[0].delivered;

  menuIdItems.forEach((item) => {
    if (item.delivered < minDelivered) {
      minDelivered = item.delivered;
    }
  });

  return minDelivered;
};

function Realtime() {
  // Subscribe ai cambiamenti delle quantities nella collection products
  const { productId, quantities } = useProductQuantitiesChanges('product', 'quantities');

  const [initProducts, setInitProducts] = useState([]);
  const [productsStats, setProductsStats] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());

  const [menuReload, setMenuReload] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // I default sono in  linea con quelli di TimeSelector in ms! setHours ritorna ms
  const [selectedStart, setSelectedStart] = useState(new Date().setHours(16, 0));
  const [selectedEnd, setSelectedEnd] = useState(new Date().setHours(23, 59));
  const [selectedTurnTime, setSelectedTurnTime] = useState(new Date().setHours(20, 0));
  // const [turns, setTurns] = useState(2); // Pranzo & Cena

  // const [isMobile] = useMediaQuery('(max-width: 600px)');
  useBreakpointValue({ base: true, md: false });
  
  // Controllo specifico per iPhone
  const isIPhone = typeof window !== 'undefined' && 
    /iPhone/i.test(navigator.userAgent);

  const unsubscribe = useRef(null);

  const subscribe = (startMs, endMs /*, _turns*/) => {
    const q = query(
      collection(db, ORDER),
      // where('status', '==', 'paid'), // Bisogna creare un indice composite
      where('selectedDate', '>=', new Date(startMs)),
      where('selectedDate', '<', new Date(endMs)),
    );

    if (unsubscribe.current) {
      // console.log('UNSUBSCRIBE');
      unsubscribe.current();
    }

    // console.log('=====>>>> SUBSCRIBE!!!', new Date(startMs), new Date(endMs));

    unsubscribe.current = onSnapshot(q, (querySnapshot) => {
      // console.log('***TUTTI I PRODOTTI NEL MENU***');
      const clone = initProducts.map((item) => {
        // console.log('INIT PRODUCTS', item);
        return { ...item };
      });
      querySnapshot.forEach((doc) => {
        const _doc = doc.data();
        // Per ogni elemento dell'order, recupero le loro info dal menu reference (clone) e aggiorno le statistiche
        // console.log('order', _doc.order);
        _doc.order.forEach((pc) => {
          // console.log('ORDER-ITEM pc = product/combo', pc);
          // Se elemento di un combo con price = 0 allora skip
          if (pc.menuPrice === 0) return;
          const pStat = clone.find((_cloned) => {
            // Combo Main, cerco per menuId
            if (pc.menuId) {
              pc.delivered = calculateMinDelivered(_doc.order, pc.menuId);
              return _cloned.productId === pc.menuId;
            }
            // Product, cerco per productId
            return _cloned.productId === pc.productId;
          });
          // console.log('pStat prodotto clonato dal menu', pStat);
          // console.log('_doc.status', _doc.status);
          // Se paymentType è cash/card, l'ordine è stato fatto da cashier, altrimenti 
          const statusKey =
            _doc.paymentType === 'cash' || _doc.paymentType === 'card' ? 'cashier' : _doc.status;
          // Gestisce gli ordini con satus non previsto (ad esempio il deprecato admin)
          if (!pStat[statusKey]) {
            // console.log('pStat[statusKey]', statusKey, pc);
            return
          }
          pStat[statusKey] = {
            quantity: pStat[statusKey].quantity + pc.quantity,
            delivered: pStat[statusKey].delivered + pc.delivered,
          };
        });
      });
      setProductsStats([...clone]);
    });
  };

  // COPY PASTE ON ALL PROTECTED ROUTES
  const [user] = useAuthState(auth);
  if (!user || user.displayName !== 'admin') return <Navigate to='/dashboard' replace={true} />;

  // INIT PRODUCTS FROM MENU
  useEffect(() => {
    const _initProducts = async () => {
      setIsLoading(true);
      const m = await getMenu(() => {});
      setIsLoading(false);
      menuUtils.orderByPosition(m);
      // Trasformo il menu con i dati che mi servono per il realtime
      const _resProducts = m.products.map((p) => ({
        isCombo: false,
        productId: p.id,
        title: p.title,
        isAvailable: p.isAvailable,
        quantities: p.quantities,
        paid: { quantity: 0, delivered: 0 }, // pagati su stripe
        unpaid: { quantity: 0, delivered: 0 }, // non pagati su stripe - serve per evitare errori
        canceled: { quantity: 0, delivered: 0 }, // ???
        cashier: { quantity: 0, delivered: 0 }, // rendicontati da cashier
      }));

      const _resCombo = m.combos.map((c) => {
        return {
          isCombo: true,
          products: c.products, // extra field
          productId: c.id,
          title: c.title,
          isAvailable: c.isAvailable,
          paid: { quantity: 0, delivered: 0 }, // pagati su stripe
          unpaid: { quantity: 0, delivered: 0 }, // non pagati su stripe - serve per evitare errori
          canceled: { quantity: 0, delivered: 0 }, // ???
          cashier: { quantity: 0, delivered: 0 }, // rendicontati da cashier
        };
      });

      setInitProducts([..._resCombo, ..._resProducts]);
    };
    _initProducts();
  }, [menuReload]);

  // UPDATE QUANTITIES FOR PRODUCT (useProductQuantitiesChanges)
  useEffect(() => {
    if (initProducts.length === 0) return;
    if (!productId || !Array.isArray(quantities)) return;
    setInitProducts((prev) =>
      prev.map((p) => (p.productId === productId ? { ...p, quantities } : p)),
    );
  }, [productId, quantities, initProducts.length]);

  // Aggiorna il campo 'qtyForDate' DI PRODUCTS E COMBO
  // Se la data scelta non è tra quella dei prodotti, ritorna 0
  useEffect(() => {
    if (initProducts.length === 0) return;
    const refDate = new Date(selectedDate).setHours(
      new Date(selectedTurnTime).getHours(),
      new Date(selectedTurnTime).getMinutes(),
      0,
      0,
    );
    initProducts
      .filter((ppp) => !ppp.isCombo)
      .forEach((ppp) => {
        const matchingProduct = (Array.isArray(ppp.quantities) ? ppp.quantities : []).find((item) => {
          const dateA = new Date(item.date);
          const dateB = new Date(refDate);
          return Math.floor(dateA.getTime() / 1000) === Math.floor(dateB.getTime() / 1000);
        });

        ppp.qtyForDate = matchingProduct ? matchingProduct.qty : 0;
      });

    initProducts
      .filter((ccc) => ccc.isCombo)
      .forEach((ccc) => {
        const pIds = ccc.products.map((p) => p.id);
        const minQtyForDate = pIds.reduce((minQty, productId) => {
          const product = initProducts
            .filter((ppp) => !ppp.isCombo)
            .find((p) => p.productId === productId);
          if (product && product.qtyForDate < minQty) {
            return parseInt(product.qtyForDate);
          }
          return parseInt(minQty);
        }, Infinity);
        ccc.qtyForDate = minQtyForDate;
      });
    // Aggiorno le product stats qtyForDate
    setProductsStats((prevStats) =>
      prevStats.map((pStat) => {
        const clone = initProducts.find((i) => i.productId === pStat.productId);
        return {
          ...pStat,
          qtyForDate: clone ? clone.qtyForDate : pStat.qtyForDate,
        };
      }),
    );
  }, [initProducts, selectedDate, selectedTurnTime, productId]);

  // NEW UNSUBSCRIBE/SUBSCRIBE IF INIT PRODUCTS OR DATE CHANGE
  useEffect(() => {
    if (initProducts.length === 0) return;
    const start = new Date(selectedDate).setHours(
      new Date(selectedStart).getHours(),
      new Date(selectedStart).getMinutes(),
    );
    const end = new Date(selectedDate).setHours(
      new Date(selectedEnd).getHours(),
      new Date(selectedEnd).getMinutes(),
    );
    subscribe(start, end /*, turns*/);
  }, [initProducts, selectedDate, selectedStart, selectedEnd /*, turns*/]);

  const headerStyle = {
    writingMode: 'vertical-rl', 
    textAlign: 'center',
    transform: isIPhone ? 'rotate(90deg)' : 'none',
    whiteSpace: 'nowrap',
    height: isIPhone ? '80px' : 'auto',
    width: isIPhone ? '25px' : 'auto',
    fontSize: isIPhone ? '12px' : '14px',
    padding: isIPhone ? '2px' : '4px'
  };

  return (
    <VStack w='100%'>
      <Heading mt={5} mb={0} color={'white'}>
        Dati In Tempo Reale
      </Heading>

      <Text mb={3}>{process.env.REACT_APP_PROJECT_ID}</Text>
      <Button mb={3} as={Link} to='/dashboard'>
        Dashboard
      </Button>

      <Stack
        direction={['column', 'row']}
        mb={3}
        w={'100%'}
        border='0px solid teal'
        alignItems={['flex-start', 'center']}
      >
        <Text minW={150}>Seleziona il giorno</Text>
        <DatePicker
          // showTimeSelect
          dateFormat='MMMM d, yyyy'
          showPopperArrow={false}
          selected={selectedDate}
          onChange={(_date) => {
            setSelectedDate(_date);
            // setMenuReload(!menuReload)
          }}
          customInput={<CustomDatePicker />}
        />
        <TurnSelector
          onTurnChange={(start, end, turnTime /*, turns*/) => {
            setSelectedStart(start);
            setSelectedEnd(end);
            setSelectedTurnTime(turnTime);
            // setMenuReload(!menuReload)
            // setTurns(turns);
          }}
        />
      </Stack>
      {/* <Text mb={3}>Dati di oggi {new Date().toLocaleDateString()}</Text> */}
      <Box w='100%' border={'0px solid teal'} borderRadius={15} p={5} mb={10} color='white'>
        {/* <TableContainer> */}
        <Table size='sm'>
          <Thead>
            <Tr>

              <Th style={headerStyle}></Th>
              <Th style={headerStyle}></Th>
              <Th style={headerStyle}>Disponibili</Th>
              <Th style={headerStyle}>Consegnati</Th>
              <Th style={headerStyle}>Online</Th>
              <Th style={headerStyle}>Casse</Th>
              {/* <Th style={headerStyle}>%</Th> */}
              <Th style={headerStyle}>Da consegnare</Th>
            </Tr>
          </Thead>
          <Tbody >
            {productsStats.length === 0 && <Spinner />}
            {productsStats.map((pStat, i) => {
              const refDate = new Date(selectedDate).setHours(
                new Date(selectedTurnTime).getHours(),
                new Date(selectedTurnTime).getMinutes(),
                0,
              );
              // console.log('pStat',pStat)
              return (
                <ProductStatus
                  key={i}
                  {...pStat}
                  refDate={refDate}
                  onMaxUpdate={() => {
                    setMenuReload(!menuReload);
                  }}
                />
              );
            })}
          </Tbody>
        </Table>
        {/* </TableContainer> */}
      </Box>
      {/* <Box w='100%' border={'3px solid teal'} borderRadius={15} p={5} mb={10} color='white'>
        {productsStats.length === 0 && <Spinner />}
        {productsStats.map((pStat, i) => {
          return <ProductStatus key={i} {...pStat} />;
        })}
      </Box> */}
      <ModalSpinner isOpen={isLoading} />
      <Footer />
    </VStack>
  );
}

export default Realtime;
